import React, {useEffect, useState} from "react";
import AppBar from "../components/app_bar/AppBar";
import Drawer from "../components/Drawer";
import { 
    Typography, Container, CssBaseline, Box, Toolbar, Grid, Link,
    Divider, Chip,
} from '@mui/material';
import { useParams, useLocation } from "react-router-dom";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FlagIcon from '@mui/icons-material/Flag';
import StarIcon from '@mui/icons-material/Star';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import GroupsIcon from '@mui/icons-material/Groups';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube, faBilibili, faXTwitter } from '@fortawesome/free-brands-svg-icons';

import * as CONSTANTS from "../constants";

import { view_wotageishi } from "../services/topic_view";


const WotageishiDetails = () => {

    const location = useLocation();
    let {lang, wotageishiId} = useParams();

    const [wotageishi, setWotageishi] = useState();
    const [drawerOpen, setDrawerOpen] = useState(false);
            
    const handleDrawerOpen = (open) => {
        if (open === undefined) {
            setDrawerOpen(!drawerOpen)
        } else {
            setDrawerOpen(open);
        }
    }

    const fetchWotageishi = async () => {
        const [status, result] = await view_wotageishi(lang, wotageishiId.toString());
        if (status === 200) {
            setWotageishi(result);
        }
    }
    
    useEffect(() => {
        fetchWotageishi();
    }, [location.pathname, location.search]);


    if (wotageishi) {

        document.title = `${wotageishi.name} | ${CONSTANTS.DRAWER.WOTAGEISHI[lang]} - Wotagei Zone`

        let active_regions = []
        wotageishi.active_regions.forEach(active_region => {
            CONSTANTS.COUNTRY_LIST.forEach(country => {
                if (country.code === active_region) {
                    active_regions.push(country.label[lang]);
                    return;
                }
            })
        }); 

        return (
            <Box>
                <CssBaseline />
                <AppBar lang={lang} handleDrawerOpen={handleDrawerOpen}/>
                <Box sx={{ display: 'flex' }}>
                    <Drawer lang={lang} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
                    <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                        <Toolbar />
                        <Container maxWidth="md">
                            {/* Breadcrumb and Title */}
                            <Typography>
                                <Link
                                    href={`/${lang}/wotageishi`}
                                    underline="hover"
                                    color={"textPrimary"}
                                >
                                    {CONSTANTS.DRAWER.WOTAGEISHI[lang]}
                                </Link>
                                {` > ${wotageishi.name}`}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Typography 
                                variant="h4" 
                                gutterBottom 
                                sx={{
                                    textAlign: { xs: 'center', md: 'left' },
                                    fontWeight: 700
                                }}
                                mt={5}
                                mb={4}
                            >
                                {wotageishi.name}
                            </Typography>

                            {/* Main Info Section */}
                            <Box sx={{ mb: 4 }}>
                                <Grid container spacing={3}>
                                    {/* Experience */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <AccessTimeIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.WOTAGEISHI_DETAILS.EXPERIENCE[lang]}:</Typography>
                                            <Typography variant="body2">{wotageishi.experience !== "" ? ` ${wotageishi.experience}${lang === 'en' ? ' year' : ' 年'}` : " N/A"}</Typography>
                                        </Box>
                                    </Grid>
                                    {/* Country */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <FlagIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.WOTAGEISHI_FILTER.COUNTRY[lang]}:</Typography>
                                            <Typography variant="body2">{active_regions.join(', ')}</Typography>
                                        </Box>
                                    </Grid>
                                    {/* Organization */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <GroupsIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.WOTAGEISHI_DETAILS.ORGANIZATION[lang]}:</Typography>
                                            <Typography variant="body2">{wotageishi.organizations.join(', ')}</Typography>
                                        </Box>
                                    </Grid>
                                    {/* Markings */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                            <BookmarkIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.WOTAGEISHI_DETAILS.MARKINGS[lang]}:</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {wotageishi.markings.map((marking) => (
                                                    <Chip
                                                        key={marking.toString()} 
                                                        label={marking.toString()}
                                                        variant="outlined"
                                                        sx={{
                                                            borderRadius: '0.5rem',
                                                            borderColor: '#ccc',
                                                            fontSize: '0.85rem',
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    </Grid>
                                    {/* Famous Wazaren */}
                                    <Grid item xs={12} md={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <StarIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.WOTAGEISHI_DETAILS.FAMOUS_WAZAREN[lang]}:</Typography>
                                            <Typography variant="body2">{wotageishi.original_wazarens.join(', ')}</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Divider sx={{ my: 2 }} />

                            {/* Social Media Section */}
                            <Box sx={{ mt: 4 }}>
                                <Typography variant="h6" fontWeight={700} mb={2}>
                                    {CONSTANTS.WOTAGEISHI_DETAILS.SOCIAL_MEDIA[lang]}
                                </Typography>
                                <Grid container spacing={2}>
                                    {/* X (Twitter) */}
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FontAwesomeIcon icon={faXTwitter} size="lg" />
                                            <Typography variant="subtitle2" fontWeight={600}>X</Typography>
                                        </Box>
                                        <Link 
                                            href={wotageishi.official_account.x}
                                            rel="noreferrer"
                                            target="_blank"
                                            sx={{ wordBreak: 'break-all' }}
                                        >
                                            <Typography 
                                                color="textPrimary"
                                                sx={{ 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                }}
                                                maxWidth="100%"
                                            >
                                                {wotageishi.official_account.x}
                                            </Typography>
                                        </Link>
                                    </Grid>
                                    {/* YouTube */}
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FontAwesomeIcon icon={faYoutube} size="lg" />
                                            <Typography variant="subtitle2" fontWeight={600}>YouTube</Typography>
                                        </Box>
                                        <Link 
                                            href={wotageishi.official_account.youtube}
                                            rel="noreferrer"
                                            target="_blank"
                                            sx={{ wordBreak: 'break-all' }}
                                        >
                                            <Typography 
                                                color="textPrimary"
                                                sx={{ 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                }}
                                                maxWidth="100%"
                                            >
                                                {wotageishi.official_account.youtube}
                                            </Typography>
                                        </Link>
                                    </Grid>
                                    {/* Bilibili */}
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FontAwesomeIcon icon={faBilibili} size="lg" />
                                            <Typography variant="subtitle2" fontWeight={600}>Bilibili</Typography>
                                        </Box>
                                        <Link 
                                            href={wotageishi.official_account.bilibili}
                                            rel="noreferrer"
                                            target="_blank"
                                            sx={{ wordBreak: 'break-all' }}
                                        >
                                            <Typography 
                                                color="textPrimary"
                                                sx={{ 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                }}
                                                maxWidth="100%"
                                            >
                                                {wotageishi.official_account.bilibili}
                                            </Typography>
                                        </Link>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                        </Container>
                    </Box>
                </Box>
            </Box>
        );
    }
    
};
 
export default WotageishiDetails;